import { ChangeEventHandler, Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Button, TextField } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import TransportAgentWarning, { WarningAction } from "./TransportAgentWarning";
import apiRequest from "utils/apiRequestWithErrorCode";
import { FieldWithButtonContainer } from "../common/styled";

import { ResponseOrganization } from "@dashboard-v3/api";
import { Form } from "../types";
import FieldActions from "../common/FieldActions";

type ModalState = {
  open: boolean;
  type: WarningAction;
};

export interface Props {
  gateway: ResponseOrganization["gateway"];
  onNewTransportAgent: (organization: ResponseOrganization) => void;
  updateForm: Dispatch<SetStateAction<Form>>;
}

export default function TransportAgents(props: Props) {
  const { t } = useTranslation("orgAndDomains");
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const { gateway, onNewTransportAgent, updateForm } = props;
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    type: null,
  });

  const handleAction = (status: boolean, action: WarningAction) => e => {
    const { previousAllowedTransportAgent } = gateway;
    if (action === WarningAction.GENERATE && previousAllowedTransportAgent) {
      enqueueSnackbar(t("org.transportAgent.generate.warning"), {
        variant: "info",
      });
    } else {
      setModalState({ open: status, type: action });
    }
  };

  async function generateTransportAgent() {
    const { type } = modalState;
    setSaving(true);
    try {
      const method = type === WarningAction.GENERATE ? "POST" : "DELETE";
      const path = "/organization/transport-agent";
      const response = await apiRequest<ResponseOrganization>(method, path);
      onNewTransportAgent(response);
    } catch (e) {
      const key =
        type === WarningAction.GENERATE ? "generate.error" : "remove.error";

      enqueueSnackbar(t(`org.transportAgent.${key}`), { variant: "error" });
    } finally {
      setSaving(false);
      setModalState({ open: false, type: null });
    }
  }

  const onProcessTAOnly: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, checked } = e.target;

    updateForm(prev => {
      const update = { ...prev };
      update.gateway[name] = checked;
      return update;
    });
  };

  return (
    <>
      <FieldWithButtonContainer>
        <TextField
          variant="outlined"
          fullWidth
          name="allowedTransportAgent"
          label={t("org.transportAgent.field.label")}
          value={gateway.allowedTransportAgent}
          InputProps={{
            disabled: true,
            inputProps: { "data-testid": "orgdomains__transport-agent-input" },
            endAdornment: (
              <FieldActions
                onGenerate={handleAction(true, WarningAction.GENERATE)}
                value={gateway.allowedTransportAgent}
                copyTooltip={t("org.transportAgent.copy.tooltip")}
                generateTooltip={t("org.transportAgent.generate.tooltip")}
              />
            ),
          }}
        />
      </FieldWithButtonContainer>
      {gateway.previousAllowedTransportAgent && (
        <FieldWithButtonContainer>
          <TextField
            variant="outlined"
            name="previousAllowedTransportAgent"
            label={t("org.transportAgent.previous.label")}
            value={gateway.previousAllowedTransportAgent}
            disabled
          />
          <Button
            variant="text"
            color="secondary"
            onClick={handleAction(true, WarningAction.REMOVE)}
          >
            {t("org.transportAgent.remove.btn")}
          </Button>
        </FieldWithButtonContainer>
      )}
      <div style={{ maxWidth: "60%" }}>
        <SwitchField
          name="onlyProcessRulesWithTA"
          label={t("org.transportAgent.processTAOnly.label")}
          description={t("org.transportAgent.processTAOnly.desc")}
          value={gateway.onlyProcessRulesWithTA}
          onChange={onProcessTAOnly}
        />
      </div>
      {modalState.open && (
        <TransportAgentWarning
          type={modalState.type}
          isOpen={modalState.open}
          loading={saving}
          onClose={() => setModalState(prev => ({ ...prev, open: false }))}
          onConfirm={generateTransportAgent}
        />
      )}
    </>
  );
}
